import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, ButtonBase, CircularProgress, Snackbar, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutlined';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import { api, type Announcement, type DeckSummary } from '../../lib/api';
import { ASSETS } from '../../lib/brand';
import { navigate, paths } from '../../lib/router';
import { useAuthStore } from '../../store/authStore';
import { useDepartmentStore } from '../../store/departmentStore';
import { AnnouncementBoard } from './AnnouncementBoard';
import { DeckCard, DeckGridSkeleton, EmptyState, PageHeader, Section } from './portalParts';
import { deckGrid, timeAgo } from './portalStyles';
import { useCreateDeck } from '../../hooks/useCreateDeck';
import { TemplatePicker } from './TemplatePicker';

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

function SeeAll({ to, label = 'See all' }: { to: string; label?: string }) {
  return (
    <Button size="small" color="neutral" endIcon={<ArrowForwardIcon />} onClick={() => navigate(to)}>
      {label}
    </Button>
  );
}

export function CreateButtons() {
  const aiEnabled = useAuthStore((s) => s.aiEnabled);
  const { create, creating, error, setError } = useCreateDeck();
  const [templatesOpen, setTemplatesOpen] = useState(false);
  return (
    <>
      <Snackbar open={Boolean(error)} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" variant="filled" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      <Button variant="outlined" color="neutral" disabled={creating} onClick={() => setTemplatesOpen(true)}>
        From template
      </Button>
      {aiEnabled && (
        <Button
          variant="outlined"
          color="neutral"
          disabled={creating}
          startIcon={<AutoAwesomeOutlinedIcon color="primary" />}
          onClick={() => void create(true)}
        >
          Draft with AI
        </Button>
      )}
      <Button
        variant="contained"
        disabled={creating}
        startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
        onClick={() => void create(false)}
      >
        New deck
      </Button>
      <TemplatePicker open={templatesOpen} onClose={() => setTemplatesOpen(false)} />
    </>
  );
}

export function HomePage() {
  const user = useAuthStore((s) => s.user);
  const departments = useDepartmentStore((s) => s.departments);
  const departmentsLoaded = useDepartmentStore((s) => s.loaded);
  const [announcements, setAnnouncements] = useState<Announcement[] | null>(null);
  const [shared, setShared] = useState<DeckSummary[] | null>(null);
  const [mine, setMine] = useState<DeckSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadAnnouncements = useCallback(
    () =>
      api.announcements
        .list()
        .then((r) => setAnnouncements(r.announcements))
        .catch((err) => setError(err instanceof Error ? err.message : 'Could not load announcements')),
    [],
  );

  useEffect(() => {
    void loadAnnouncements();
    api
      .listDecks('shared')
      .then((r) => setShared(r.decks))
      .catch(() => setShared([]));
    api
      .listDecks('mine')
      .then((r) => setMine(r.decks))
      .catch(() => setMine([]));
  }, [loadAnnouncements]);

  const firstName = (user?.name || user?.email.split('@')[0] || '').split(' ')[0];

  return (
    <>
      <PageHeader
        title={`${greeting()}, ${firstName}`}
        description="What's new at AST: announcements, department folders and shared slides."
        actions={<CreateButtons />}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <AnnouncementBoard
        title="Latest announcements"
        icon={<CampaignOutlinedIcon />}
        items={announcements && announcements.slice(0, 3)}
        onChanged={() => void loadAnnouncements()}
        emptyText="No announcements yet."
        action={announcements && announcements.length > 3 ? <SeeAll to={paths.announcements()} /> : undefined}
      />

      <Section title="Departments" icon={<FolderOutlinedIcon />}>
        {!departmentsLoaded ? (
          <DeckGridSkeleton count={3} />
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2 }}>
            {departments.map((d) => (
              <ButtonBase
                key={d.id}
                onClick={() => navigate(paths.dept(d.id))}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  textAlign: 'left',
                  gap: 1,
                  p: 2,
                  borderRadius: 2.5,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  transition: 'box-shadow 150ms, border-color 150ms',
                  '&:hover': { borderColor: 'chrome.border', boxShadow: '0 6px 20px -8px rgba(16,24,40,0.2)' },
                }}
              >
                <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                  <Box
                    sx={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      bgcolor: 'chrome.subtle',
                    }}
                  >
                    <FolderOutlinedIcon sx={{ color: d.color }} />
                  </Box>
                  <Typography variant="subtitle2" noWrap>
                    {d.name}
                  </Typography>
                </Stack>
                {d.description && (
                  <Typography variant="caption" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {d.description}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 'auto' }}>
                  {d.deck_count} {d.deck_count === 1 ? 'deck' : 'decks'} · {d.announcement_count}{' '}
                  {d.announcement_count === 1 ? 'announcement' : 'announcements'}
                  {d.last_updated ? ` · ${timeAgo(d.last_updated)}` : ''}
                </Typography>
              </ButtonBase>
            ))}
          </Box>
        )}
      </Section>

      <Section title="Recently shared" icon={<PeopleOutlineIcon />}>
        {shared === null ? (
          <DeckGridSkeleton />
        ) : shared.length ? (
          <Box sx={deckGrid}>
            {shared.slice(0, 8).map((d) => (
              <DeckCard key={d.id} deck={d} onOpen={() => navigate(paths.view(d.id))} />
            ))}
          </Box>
        ) : (
          <EmptyState>
            Nothing shared yet. Open a deck and use <strong>Share</strong> to publish it to a department folder.
          </EmptyState>
        )}
      </Section>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        <Section
          title="Your recent decks"
          icon={<SlideshowOutlinedIcon />}
          action={mine && mine.length > 0 ? <SeeAll to={paths.mine()} /> : undefined}
        >
          {mine === null ? (
            <DeckGridSkeleton count={2} />
          ) : mine.length ? (
            <Box sx={{ ...deckGrid, gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
              {mine.slice(0, 4).map((d) => (
                <DeckCard key={d.id} deck={d} onOpen={() => navigate(paths.edit(d.id))} />
              ))}
            </Box>
          ) : (
            <EmptyState>You haven't made a deck yet. Start with New deck.</EmptyState>
          )}
        </Section>

        <Section title="Brand kit" icon={<PaletteOutlinedIcon />}>
          <ButtonBase
            onClick={() => navigate(paths.assets())}
            sx={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              borderRadius: 2.5,
              overflow: 'hidden',
              border: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              '&:hover': { borderColor: 'chrome.border' },
            }}
          >
            <Box
              sx={{
                aspectRatio: '16 / 7',
                backgroundImage: `url(${ASSETS.coverBackground})`,
                backgroundSize: 'cover',
                display: 'flex',
                alignItems: 'center',
                px: 3,
              }}
            >
              <Box component="img" src={ASSETS.logoWhite} alt="" sx={{ height: 44 }} />
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2">Company assets</Typography>
              <Typography variant="caption">Logos, the PowerPoint template, brand colours, fonts and approved images.</Typography>
            </Box>
          </ButtonBase>
        </Section>
      </Box>
    </>
  );
}
